import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Collapse, Form, Modal, Pagination, Spinner } from 'react-bootstrap';
import Swal from '../../../../helpers/swal';
import surveyService from '../../../../../services/survey.service';
import Loading from '../../../../common/Loading';
import ProcessQueueFilters from '../../../../process/ProcessQueueFilters';
import { formatStatusLabel, truncateText, toProperCase } from '../../../../process/processQueueUtils';
const SEARCH_FIELDS = [
    { value: 'title', label: 'Title' },
    { value: 'description', label: 'Description' },
    { value: 'category', label: 'Category' },
];

const SORT_FIELDS = [
    { value: 'createdAt', label: 'Created At' },
    { value: 'modifiedAt', label: 'Modified At' },
    { value: 'title', label: 'Title' },
    { value: 'category', label: 'Category' },
    { value: 'status', label: 'Status' },
    { value: 'startDate', label: 'Start Date' },
    { value: 'endDate', label: 'End Date' },
];

const STATUS_OPTIONS = ['DRAFT', 'ACTIVE', 'CLOSED', 'ARCHIVED'];

const STATUS_BADGE_CLASS = {
    DRAFT: 'clinic-surveys-status-badge--draft',
    ACTIVE: 'clinic-surveys-status-badge--active',
    CLOSED: 'clinic-surveys-status-badge--closed',
    ARCHIVED: 'clinic-surveys-status-badge--archived',
};

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const formatDateRange = (startDate, endDate) => {
    if (!startDate && !endDate) return '—';
    if (startDate && endDate) return `${startDate} – ${endDate}`;
    return startDate || endDate;
};

const formatResponses = (targetResponse, maxResponse) => {
    if (targetResponse == null && maxResponse == null) return '—';
    const parts = [];
    if (targetResponse != null) parts.push(`${targetResponse} target`);
    if (maxResponse != null) parts.push(`${maxResponse} max`);
    return parts.join(' · ');
};

function ClinicSurvey() {
    const { clinicId } = useParams();
    const navigate = useNavigate();
    const [surveyList, setSurveyList] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [searchPanelOpen, setSearchPanelOpen] = useState(false);

    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('DESC');
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [searchField, setSearchField] = useState('title');
    const [statusFilter, setStatusFilter] = useState('');

    const [showInstallModal, setShowInstallModal] = useState(false);
    const [templateList, setTemplateList] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [templatesError, setTemplatesError] = useState(null);
    const [templateSearch, setTemplateSearch] = useState('');
    const [appliedTemplateSearch, setAppliedTemplateSearch] = useState('');
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [installing, setInstalling] = useState(false);

    const fetchSurveys = useCallback(() => {
        setLoading(true);
        setError(null);

        surveyService
            .getClinicSurveys(clinicId, currentPage, itemsPerPage, {
                sortBy,
                sortOrder,
                searchField,
                searchTerm: appliedSearch,
                statusFilter,
            })
            .then((res) => {
                const content = res.data.content || [];
                setSurveyList(content);
                setTotalElements(res.data.totalElements || 0);
                setTotalPages(res.data.totalPages || 0);
                if (content.length === 0 && (appliedSearch || statusFilter)) {
                    setError('No surveys match your filters.');
                }
            })
            .catch((err) => {
                console.error(err);
                setError('Failed to load clinic surveys. Please try again.');
                setSurveyList([]);
                setTotalElements(0);
                setTotalPages(0);
            })
            .finally(() => setLoading(false));
    }, [clinicId, currentPage, itemsPerPage, sortBy, sortOrder, appliedSearch, searchField, statusFilter]);

    useEffect(() => {
        fetchSurveys();
    }, [fetchSurveys]);

    const loadTemplates = useCallback(() => {
        setTemplatesLoading(true);
        setTemplatesError(null);

        surveyService
            .getSurveyTemplates(0, 50, {
                sortBy: 'title',
                sortOrder: 'ASC',
                searchField: 'title',
                searchTerm: appliedTemplateSearch,
            })
            .then((res) => {
                const content = res.data.content || [];
                setTemplateList(content);
                if (content.length === 0) {
                    setTemplatesError(
                        appliedTemplateSearch
                            ? 'No survey templates match your search.'
                            : 'No survey templates are available to install.'
                    );
                }
            })
            .catch((err) => {
                console.error(err);
                setTemplatesError('Failed to load survey templates. Please try again.');
                setTemplateList([]);
            })
            .finally(() => setTemplatesLoading(false));
    }, [appliedTemplateSearch]);

    useEffect(() => {
        if (!showInstallModal) return;
        loadTemplates();
    }, [showInstallModal, loadTemplates]);

    const handleOpenInstallModal = () => {
        setTemplateSearch('');
        setAppliedTemplateSearch('');
        setSelectedTemplateId('');
        setTemplatesError(null);
        setShowInstallModal(true);
    };

    const handleCloseInstallModal = () => {
        if (installing) return;
        setShowInstallModal(false);
    };

    const handleTemplateSearchSubmit = (e) => {
        e.preventDefault();
        setSelectedTemplateId('');
        setAppliedTemplateSearch(templateSearch);
    };

    const handleInstallSurvey = async () => {
        const selectedTemplate = templateList.find((template) => template.id === selectedTemplateId);
        if (!selectedTemplate) return;

        const result = await Swal.fire({
            title: 'Install survey template?',
            html: `"<strong>${selectedTemplate.title || 'Untitled'}</strong>" will be copied to this clinic as a new <strong>DRAFT</strong> survey.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: '<i class="bi bi-download"></i> Install',
            cancelButtonText: '<i class="bi bi-x"></i> Cancel',
            reverseButtons: true,
            focusCancel: true,
            customClass: {
                title: 'card-title',
                confirmButton: 'btn btn-primary btn-sm',
                cancelButton: 'btn btn-secondary btn-sm',
                actions: 'gap-3',
            },
            buttonsStyling: false,
        });

        if (!result.isConfirmed) return;

        setInstalling(true);

        try {
            await surveyService.installSurveyTemplate({
                surveyTemplateId: selectedTemplate.id,
                clinicId,
            });

            setShowInstallModal(false);
            await Swal.fire({
                title: 'Installed!',
                text: 'Survey template has been installed for this clinic.',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false,
                customClass: { title: 'card-title' },
                buttonsStyling: false,
            });

            if (currentPage === 0) {
                fetchSurveys();
            } else {
                setCurrentPage(0);
            }
        } catch (err) {
            Swal.fire({
                title: 'Install failed',
                text: err.response?.data?.message || 'Failed to install survey template. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
                customClass: {
                    title: 'card-title',
                    confirmButton: 'btn btn-secondary btn-sm',
                },
                buttonsStyling: false,
            });
        } finally {
            setInstalling(false);
        }
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(0);
        setAppliedSearch(searchTerm);
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setAppliedSearch('');
        setStatusFilter('');
        setCurrentPage(0);
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(0);
    };

    const toggleRow = (id) => {
        setExpandedRowId((prev) => (prev === id ? null : id));
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        Swal.fire({
            icon: 'success',
            title: 'Copied!',
            text: 'Survey ID copied to clipboard',
            timer: 1500,
            showConfirmButton: false,
            position: 'top-end',
            toast: true,
        });
    };

    const handleOpenSurvey = (survey) => {
        navigate(`/clinics/${clinicId}/surveys/${survey.id}/edit`);
    };

    const handleDeleteSurvey = async (survey) => {
        const result = await Swal.fire({
            title: 'Delete survey?',
            text: `"${survey.title || 'This survey'}" will be removed from this clinic. This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: '<i class="bi bi-check"></i> Yes, delete',
            cancelButtonText: '<i class="bi bi-x"></i> Cancel',
            reverseButtons: true,
            focusCancel: true,
            customClass: {
                title: 'card-title',
                confirmButton: 'btn btn-danger btn-sm',
                cancelButton: 'btn btn-secondary btn-sm',
                actions: 'gap-3',
            },
            buttonsStyling: false,
        });

        if (!result.isConfirmed) return;

        try {
            await surveyService.deleteClinicSurvey(clinicId, survey.id);
            await Swal.fire({
                title: 'Deleted!',
                text: 'Survey has been removed from this clinic.',
                icon: 'success',
                timer: 2000,
                showConfirmButton: false,
                customClass: { title: 'card-title' },
                buttonsStyling: false,
            });
            setExpandedRowId(null);
            if (currentPage === 0) {
                fetchSurveys();
            } else {
                setCurrentPage(0);
            }
        } catch {
            Swal.fire({
                title: 'Error!',
                text: 'Failed to delete survey. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
                customClass: {
                    title: 'card-title',
                    confirmButton: 'btn btn-secondary btn-sm',
                },
                buttonsStyling: false,
            });
        }
    };

    const StatusBadge = ({ status }) => (
        <span className={`clinic-surveys-status-badge ${STATUS_BADGE_CLASS[status] || 'clinic-surveys-status-badge--draft'}`}>
            {formatStatusLabel(status)}
        </span>
    );

    const startItem = surveyList.length > 0 ? currentPage * itemsPerPage + 1 : 0;
    const endItem = Math.min((currentPage + 1) * itemsPerPage, totalElements);

    const getPaginationItems = () => {
        const items = [];
        const maxVisible = 5;

        if (totalPages < 1) return items;

        items.push(
            <Pagination.Item key={1} active={currentPage === 0} onClick={() => setCurrentPage(0)}>
                1
            </Pagination.Item>
        );

        let startPage = Math.max(2, currentPage + 1);
        let endPage = Math.min(totalPages - 1, currentPage + 3);

        if (currentPage < 2) endPage = Math.min(totalPages - 1, maxVisible);
        if (currentPage > totalPages - 4) startPage = Math.max(2, totalPages - maxVisible + 1);

        if (startPage > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage + 1}
                    onClick={() => setCurrentPage(number - 1)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);

        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={currentPage === totalPages - 1}
                    onClick={() => setCurrentPage(totalPages - 1)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    return (
        <>
            <div className="d-flex align-items-end justify-content-between gap-2 mb-3">
                <div>
                    <h5 className="clinic-surveys__title mb-1">
                        <i className="bi bi-ui-checks-grid text-primary me-1"></i>
                        Clinic Surveys
                        <span className="clinic-surveys__count">
                            ({totalElements} survey{totalElements !== 1 ? 's' : ''})
                        </span>
                    </h5>
                    <p className="text-muted small mb-0">
                        Surveys installed for this clinic from survey templates.
                    </p>
                </div>
                <div className="d-flex flex-wrap gap-2 flex-shrink-0">
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleOpenInstallModal}
                    >
                        <i className="bi bi-download me-1"></i>
                        Install Survey
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setSearchPanelOpen((open) => !open)}
                        aria-expanded={searchPanelOpen}
                        title={searchPanelOpen ? 'Hide filters' : 'Show filters'}
                    >
                        <i className="bi bi-funnel me-1"></i>
                        Filter
                    </button>
                </div>
            </div>

            <Collapse in={searchPanelOpen}>
                <div className="mb-3">
                    <ProcessQueueFilters
                        searchTerm={searchTerm}
                        onSearchTermChange={setSearchTerm}
                        searchField={searchField}
                        onSearchFieldChange={setSearchField}
                        searchFields={SEARCH_FIELDS}
                        statusFilter={statusFilter}
                        onStatusFilterChange={(value) => {
                            setStatusFilter(value);
                            setCurrentPage(0);
                        }}
                        statusOptions={STATUS_OPTIONS}
                        sortBy={sortBy}
                        onSortByChange={(value) => {
                            setSortBy(value);
                            setCurrentPage(0);
                        }}
                        sortFields={SORT_FIELDS}
                        sortOrder={sortOrder}
                        onSortOrderChange={(value) => {
                            setSortOrder(value);
                            setCurrentPage(0);
                        }}
                        onSubmit={handleSearchSubmit}
                        onClear={handleClearFilters}
                        canClear={Boolean(appliedSearch || statusFilter || searchTerm)}
                    />
                </div>
            </Collapse>

            {error && surveyList.length === 0 && !loading && (
                <Alert variant="warning" className="border-0 shadow-sm mb-3">
                    {error}
                </Alert>
            )}

            <div className="notifications-panel">
                <div className="notifications-panel__header">
                    <span className="d-flex align-items-center gap-2">
                        <i className="bi bi-list-check"></i>
                        Installed Surveys
                    </span>
                </div>

                <div className="notifications-panel__body p-0">
                    <div className="clinic-surveys-toolbar px-3 pt-3">
                        <div className="d-flex align-items-center gap-2">
                            <span>Show</span>
                            <select
                                className="form-select form-select-sm clinic-surveys-page-size"
                                value={itemsPerPage}
                                onChange={handleItemsPerPageChange}
                                disabled={loading}
                            >
                                {PAGE_SIZE_OPTIONS.map((size) => (
                                    <option key={size} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </select>
                            <span>entries</span>
                        </div>
                        <div>
                            Total: <strong>{totalElements}</strong>
                        </div>
                    </div>

                    {loading ? (
                        <div className="p-4">
                            <Loading />
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="table table-bordered table-hover align-middle mb-0 process-queue-table clinic-surveys-table">
                                    <thead>
                                        <tr>
                                            <th>
                                                <i className="bi bi-card-heading"></i> Title / Category
                                            </th>
                                            <th>
                                                <i className="bi bi-flag"></i> Status
                                            </th>
                                            <th>
                                                <i className="bi bi-calendar-range"></i> Date Range
                                            </th>
                                            <th>
                                                <i className="bi bi-bar-chart"></i> Responses
                                            </th>
                                            <th>
                                                <i className="bi bi-clock-history"></i> Modified
                                            </th>
                                            <th className="text-end">
                                                <i className="bi bi-gear"></i> Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {surveyList.length > 0 ? (
                                            surveyList.map((survey) => (
                                                <React.Fragment key={survey.id}>
                                                    <tr className="clinic-surveys-row">
                                                        <td>
                                                            <div className="fw-medium">{survey.title || '—'}</div>
                                                            <small className="text-muted">
                                                                {toProperCase(survey.category) || 'Uncategorized'}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <StatusBadge status={survey.status} />
                                                        </td>
                                                        <td className="clinic-surveys-date">
                                                            {formatDateRange(survey.startDate, survey.endDate)}
                                                        </td>
                                                        <td className="clinic-surveys-meta">
                                                            {formatResponses(survey.targetResponse, survey.maxResponse)}
                                                        </td>
                                                        <td className="clinic-surveys-date">{survey.modifiedAt || survey.createdAt || '—'}</td>
                                                        <td className="text-end">
                                                            <div className="d-flex justify-content-end gap-1">
                                                                <Button
                                                                    variant="outline-primary"
                                                                    size="sm"
                                                                    onClick={() => handleOpenSurvey(survey)}
                                                                    title="Open survey"
                                                                >
                                                                    <i className="bi bi-box-arrow-up-right"></i>
                                                                </Button>
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={() => handleDeleteSurvey(survey)}
                                                                    title="Remove survey from clinic"
                                                                >
                                                                    <i className="bi bi-trash"></i>
                                                                </Button>
                                                                <Button
                                                                    variant={
                                                                        expandedRowId === survey.id
                                                                            ? 'outline-secondary'
                                                                            : 'outline-primary'
                                                                    }
                                                                    size="sm"
                                                                    onClick={() => toggleRow(survey.id)}
                                                                    aria-expanded={expandedRowId === survey.id}
                                                                    title={expandedRowId === survey.id ? 'Collapse' : 'View details'}
                                                                >
                                                                    <i
                                                                        className={`bi bi-chevron-${
                                                                            expandedRowId === survey.id ? 'up' : 'down'
                                                                        }`}
                                                                    ></i>
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td colSpan="6" className="p-0 border-0">
                                                            <Collapse in={expandedRowId === survey.id}>
                                                                <div className="clinic-surveys-detail-panel">
                                                                    <div className="row g-3">
                                                                        <div className="col-md-6">
                                                                            <div className="notifications-info-row mb-0">
                                                                                <span className="notifications-info-label">Survey ID</span>
                                                                                <div className="notifications-info-value d-flex align-items-center gap-2">
                                                                                    <span className="font-monospace small">{survey.id}</span>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="clinic-copy-btn"
                                                                                        onClick={() => copyToClipboard(survey.id)}
                                                                                        title="Copy ID"
                                                                                    >
                                                                                        <i className="bi bi-copy"></i>
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <div className="notifications-info-row mb-0">
                                                                                <span className="notifications-info-label">Category</span>
                                                                                <span className="notifications-info-value">
                                                                                    {toProperCase(survey.category) || '—'}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <div className="notifications-info-row mb-0">
                                                                                <span className="notifications-info-label">Status</span>
                                                                                <span className="notifications-info-value">
                                                                                    <StatusBadge status={survey.status} />
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <div className="notifications-info-row mb-0">
                                                                                <span className="notifications-info-label">Date Range</span>
                                                                                <span className="notifications-info-value">
                                                                                    {formatDateRange(survey.startDate, survey.endDate)}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <div className="notifications-info-row mb-0">
                                                                                <span className="notifications-info-label">Target / Max Responses</span>
                                                                                <span className="notifications-info-value">
                                                                                    {formatResponses(survey.targetResponse, survey.maxResponse)}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <div className="notifications-info-row mb-0">
                                                                                <span className="notifications-info-label">Anonymous Allowed</span>
                                                                                <span className="notifications-info-value">
                                                                                    {survey.allowAnonymous ? 'Yes' : 'No'}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <div className="notifications-info-row mb-0">
                                                                                <span className="notifications-info-label">Show Progress Bar</span>
                                                                                <span className="notifications-info-value">
                                                                                    {survey.showProgressbar ? 'Yes' : 'No'}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-md-6">
                                                                            <div className="notifications-info-row mb-0">
                                                                                <span className="notifications-info-label">Created</span>
                                                                                <span className="notifications-info-value">{survey.createdAt || '—'}</span>
                                                                            </div>
                                                                        </div>
                                                                        <div className="col-12">
                                                                            <div className="notifications-info-row mb-0">
                                                                                <span className="notifications-info-label">Description</span>
                                                                                <span className="notifications-info-value">
                                                                                    {truncateText(survey.description, 300) || '—'}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </Collapse>
                                                        </td>
                                                    </tr>
                                                </React.Fragment>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="p-0 border-0">
                                                    <div className="clinic-surveys-empty">
                                                        <i className="bi bi-ui-checks-grid"></i>
                                                        No surveys installed for this clinic yet.
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {surveyList.length > 0 && (
                                <div className="clinic-surveys-footer px-3 pb-3">
                                    <div>
                                        Showing {startItem} to {endItem} of {totalElements} entries
                                    </div>
                                    {totalPages > 1 && (
                                        <Pagination className="mb-0">
                                            <Pagination.First onClick={() => setCurrentPage(0)} disabled={currentPage === 0} />
                                            <Pagination.Prev
                                                onClick={() => setCurrentPage((p) => Math.max(p - 1, 0))}
                                                disabled={currentPage === 0}
                                            />
                                            {getPaginationItems()}
                                            <Pagination.Next
                                                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages - 1))}
                                                disabled={currentPage >= totalPages - 1}
                                            />
                                            <Pagination.Last
                                                onClick={() => setCurrentPage(totalPages - 1)}
                                                disabled={currentPage >= totalPages - 1}
                                            />
                                        </Pagination>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <Modal
                show={showInstallModal}
                onHide={handleCloseInstallModal}
                size="lg"
                centered
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header className="border-0 pb-0">
                    <Modal.Title className="clinic-surveys-modal__title">
                        <i className="bi bi-download me-2"></i>
                        Install Survey Template
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-2">
                    <p className="text-muted small mb-3">
                        Choose a survey template to copy into this clinic. The installed survey starts in DRAFT status.
                    </p>

                    <Form onSubmit={handleTemplateSearchSubmit} className="mb-3">
                        <div className="input-group input-group-sm">
                            <span className="input-group-text">
                                <i className="bi bi-search"></i>
                            </span>
                            <Form.Control
                                type="search"
                                placeholder="Search templates by title..."
                                value={templateSearch}
                                onChange={(e) => setTemplateSearch(e.target.value)}
                                disabled={templatesLoading || installing}
                            />
                            <Button
                                type="submit"
                                variant="outline-secondary"
                                disabled={templatesLoading || installing}
                            >
                                Search
                            </Button>
                        </div>
                    </Form>

                    {templatesLoading ? (
                        <div className="py-4">
                            <Loading />
                        </div>
                    ) : templatesError ? (
                        <Alert variant="warning" className="border-0 mb-0">
                            {templatesError}
                        </Alert>
                    ) : (
                        <div className="clinic-surveys-template-list">
                            {templateList.map((template) => (
                                <label
                                    key={template.id}
                                    className={`clinic-surveys-template-option${
                                        selectedTemplateId === template.id ? ' clinic-surveys-template-option--selected' : ''
                                    }`}
                                >
                                    <Form.Check
                                        type="radio"
                                        name="surveyTemplate"
                                        id={`survey-template-${template.id}`}
                                        checked={selectedTemplateId === template.id}
                                        onChange={() => setSelectedTemplateId(template.id)}
                                        disabled={installing}
                                        label={
                                            <div className="clinic-surveys-template-option__content">
                                                <div className="d-flex align-items-start justify-content-between gap-2">
                                                    <div>
                                                        <div className="fw-medium">{template.title || 'Untitled'}</div>
                                                        <small className="text-muted d-block">
                                                            {toProperCase(template.category) || 'Uncategorized'}
                                                        </small>
                                                    </div>
                                                    <StatusBadge status={template.status} />
                                                </div>
                                                {template.description && (
                                                    <small className="text-muted d-block mt-1">
                                                        {truncateText(template.description, 120)}
                                                    </small>
                                                )}
                                            </div>
                                        }
                                    />
                                </label>
                            ))}
                        </div>
                    )}

                    <div className="notifications-form-hint mt-3 mb-0">
                        <i className="bi bi-info-circle me-1"></i>
                        Installing the same template more than once creates separate clinic surveys.
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button variant="outline-secondary" size="sm" onClick={handleCloseInstallModal} disabled={installing}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleInstallSurvey}
                        disabled={!selectedTemplateId || installing || templatesLoading}
                    >
                        {installing ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-1" />
                                Installing...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-download me-1"></i>
                                Install Survey
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default ClinicSurvey;
